import { ComponentTypes } from "../Components/ComponentTypes";
import { StringUtils, engineComponent } from "neo-common";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { AnimationStyle } from "../Components/Elements/Styles/AnimationStyle";
import { StyleManager } from "../Components/Managers/StyleManager";
import { ImageElement, ImageElementAttributes } from "../Components/Elements/Graphics/ImageElement";
import { CustomUtils } from "../Utils/CustomUtils";
import { Attributes } from "../Utils/Attributes";
import { AnimationElementAttributes } from "../Components/Elements/Graphics/AnimationElement";

/**
 * Works similar to Animation element, but instead of replacing animation's frames, it layers frames onto each other
 */
@engineComponent(ComponentTypes.DisplayObject)
export class AnimationAdditiveElement extends ContainerElement {
    private playing = false;
    private _continue = false;

    private _speed = 1;
    private _delay = 0;

    private _reverse = false;
    private _loop = false;
    private _clearAfter = false;

    private _currentTime = 0;
    private _isConnectedToTicker = false;
    private _currentFrame = 0;

    private _style: AnimationStyle = null;

    private _frames: ImageElement[] = [];
    private _masks: ImageElement[] = [];

    private blendMode = 0;

    private _timerDelay: FrameTimerEventEmitter;

    constructor() {
        super();

        this._timerDelay = CreateFrameTimer("timerDelay");
        this._timerDelay.on(FrameTimerEvents.Complete, this.playMe.bind(this));
    }

    create(node: Element) {
        super.create(node);

        if (node.hasAttribute(AnimationAdditiveElementAttributes.STYLE)) {
            this._style = <AnimationStyle>(
                StyleManager.Styles[node.getAttribute(AnimationAdditiveElementAttributes.STYLE)]
            );

            let startFrame: number = null;
            let endFrame: number = null;

            if (this._style != null) {
                if (this._style.startFrame != null) {
                    startFrame = this._style.startFrame;
                }

                if (this._style.endFrame != null) {
                    endFrame = this._style.endFrame;
                }

                this._loop = this._style.loop;
                this._speed = this._style.speed ? this._style.speed : this._speed;

                if (this._style.frames) {
                    let texture: PIXI.Texture = null;
                    this._style.frames.forEach((frameName, i) => {
                        texture = PIXI.utils.TextureCache[frameName];
                        if (texture) {
                            const frame = new ImageElement(texture);
                            this._frames.push(frame);
                            frame.visible = false;
                            this.addChild(frame);
                        } else {
                            if (DEBUG) {
                                this.log(
                                    "WARNING: No texture" + frameName + " in TextureCache of AnimationAdditiveElement!",
                                );
                            }
                        }
                    });
                } else if (this._style.animation !== "") {
                    let maskSuffix: string = null;
                    if (node.getAttribute(AnimationAdditiveElementAttributes.HAS_MASK) == "true") {
                        maskSuffix = node.hasAttribute(AnimationAdditiveElementAttributes.MASK_SUFFIX)
                            ? node.getAttribute(AnimationAdditiveElementAttributes.MASK_SUFFIX)
                            : "mask_";
                    }

                    if (endFrame != null) {
                        for (let i = startFrame; i <= endFrame; i++) {
                            let val = this.zerosPad(i, 4);
                            const texture = PIXI.utils.TextureCache[this._style.animation + val];
                            if (texture) {
                                if (maskSuffix) {
                                    const maskTexture =
                                        PIXI.utils.TextureCache[this._style.animation + maskSuffix + val];
                                    if (maskTexture) {
                                        const frame = new ImageElement(maskTexture);
                                        this._masks.push(frame);
                                        frame.visible = false;
                                        this.addChild(frame);
                                    }
                                }
                                const frame = new ImageElement(texture);
                                this._frames.push(frame);
                                frame.visible = false;
                                this.addChild(frame);
                            } else {
                                if (DEBUG) {
                                    this.log(
                                        "WARNING: No texture" +
                                            this._style.animation +
                                            val +
                                            " in TextureCache of AnimationAdditiveElement!",
                                    );
                                }
                            }
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log(
                            "WARNING: animation has no endframe number. Use ImageElement with attribute Animation instead AnimationAdditiveElement!",
                        );
                    }
                }

                if (this._style.blendMode && this._style.blendMode !== this.blendMode) {
                    this.blendMode = this._style.blendMode;
                    this._frames.forEach((frame) => (frame.blendMode = this.blendMode));
                }
            }
        }

        this.updateByNode(node);
    }

    updateByNode(node: Element) {
        super.updateByNode(node);

        let oldBlendMode = this.blendMode;
        this.blendMode = CustomUtils.getBlendMode(
            this.getBlendModeByString(
                Attributes.assignStringAttribute(node, ImageElementAttributes.BLEND_MODE, this.blendMode),
            ),
        );
        if (this.blendMode !== oldBlendMode) {
            this._frames.forEach((frame) => (frame.blendMode = this.blendMode));
        }

        this._loop = Attributes.assignBooleanAttribute(node, AnimationAdditiveElementAttributes.LOOP, this._loop);
        this._reverse = Attributes.assignBooleanAttribute(
            node,
            AnimationAdditiveElementAttributes.REVERSE,
            this._reverse,
        );
        this._clearAfter = Attributes.assignBooleanAttribute(
            node,
            AnimationAdditiveElementAttributes.CLEAR_AFTER,
            this._clearAfter,
        );

        if (node.hasAttribute(AnimationAdditiveElementAttributes.SPEED)) {
            let speed = parseFloat(node.getAttribute(AnimationAdditiveElementAttributes.SPEED));
            if (speed <= 0) {
                if (DEBUG) {
                    this.log("WARNING: Speed of AnimationAdditiveElement has to be greater than zero!");
                }
            } else {
                this._speed = speed;
            }
        }

        if (node.hasAttribute(AnimationElementAttributes.STOP)) {
            this._timerDelay.stop();
            if (this.playing) {
                this.stop();
            }
        }

        if (node.hasAttribute(AnimationElementAttributes.RESET)) {
            this._timerDelay.stop();
            this.stop();
            this.reset();
        }

        const play =
            (node.hasAttribute(AnimationAdditiveElementAttributes.PLAY) &&
                StringUtils.parseBoolean(node.getAttribute(AnimationAdditiveElementAttributes.PLAY))) ||
            (node.hasAttribute(AnimationAdditiveElementAttributes.START) &&
                StringUtils.parseBoolean(node.getAttribute(AnimationAdditiveElementAttributes.START)));

        if (node.hasAttribute(AnimationAdditiveElementAttributes.CONTINUE)) {
            this._continue = StringUtils.parseBoolean(node.getAttribute(AnimationAdditiveElementAttributes.CONTINUE));
        }

        if (play && !this.playing) {
            if (node.hasAttribute(AnimationAdditiveElementAttributes.DELAY)) {
                let range = node
                    .getAttribute(AnimationAdditiveElementAttributes.DELAY)
                    .split(";")
                    .map((value) => parseInt(value));
                if (range.length > 1 && !isNaN(range[0]) && !isNaN(range[1])) {
                    this._delay = range[0] + Math.random() * (range[1] - range[0]);
                    if (this._delay < 0) {
                        this._delay = 0;
                    }
                } else {
                    if (!isNaN(range[0])) {
                        this._delay = range[0];
                    }
                }
            }

            if (this._delay > 0) {
                this._timerDelay.stop();
                this._timerDelay.start(this._delay);
            } else {
                this.playMe();
            }
        }
    }

    private playMe() {
        if (this.playing || this._isConnectedToTicker) {
            return;
        }
        if (!this._continue) {
            this.reset();
        }
        this.playing = true;
        if (this._currentFrame === null || this._currentFrame === undefined) {
            this.goToFrame(this._reverse ? this._frames.length - 1 : 0);
        }
        this._currentTime = 0;
        PIXI.ticker.shared.add(this.update, this, PIXI.UPDATE_PRIORITY.HIGH);
        this._isConnectedToTicker = true;
    }

    private update(deltaTime: number) {
        if (!this.playing) {
            return;
        }

        let elapsed = deltaTime * this._speed;

        let next = Math.floor(this._currentTime + elapsed);
        this._currentTime += elapsed;
        this._continue = false;

        if (!this._frames.length) {
            if (DEBUG) {
                this.log("WARNING: AnimationAdditiveElement " + this.uid + " has no frames!");
            }
            return;
        }

        if (this._currentTime < 0 && !this._loop) {
            this.stop();
        } else if (this._currentTime >= this._frames.length && !this._loop) {
            this.stop();
        } else if (next != this._currentFrame) {
            this.goToFrame(next);
            if (this._currentTime >= this._frames.length) {
                this.reset();
                this._currentTime -= this._frames.length;
            }
        }
    }

    private goToFrame(num: number) {
        if (this._reverse) {
            for (let i = this._currentFrame; i > num; i--) {
                if (this._masks[i]) {
                    this._masks[i].visible = false;
                }
                if (this._frames[i]) {
                    this._frames[i].visible = false;
                } else {
                    break;
                }
            }
        } else {
            for (let i = this._currentFrame; i <= num; i++) {
                if (this._masks[i]) {
                    this._masks[i].visible = true;
                }
                if (this._frames[i]) {
                    this._frames[i].visible = true;
                } else {
                    break;
                }
            }
        }
        this._currentFrame = num;
    }

    private stop() {
        if (this._clearAfter) {
            this.reset();
        }
        this.playing = false;
        this.goToFrame(this._reverse ? 0 : this._frames.length - 1);
        PIXI.ticker.shared.remove(this.update, this);
        this._isConnectedToTicker = false;
    }

    private reset() {
        this._frames.forEach((frame, index) => {
            if (this._masks[index]) {
                this._masks[index].visible = false;
            }
            frame.visible = false;
        });
        this._currentFrame = null;
    }

    clone(): AnimationAdditiveElement {
        let element: AnimationAdditiveElement = new AnimationAdditiveElement();

        element.style = this.style;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.visible = this.visible;
        element.alpha = this.alpha;
        element.name = this.name;

        element._speed = this._speed;
        element._delay = this._delay;
        element._reverse = this._reverse;
        element._loop = this._loop;
        element._clearAfter = this._clearAfter;

        this._frames.forEach((frame, i) => {
            if (this._masks[i]) {
                const clone = this._masks[i].clone();
                element._masks.push(clone);
                element.addChild(clone);
            }
            const clone = this._frames[i].clone();
            element._frames.push(clone);
            element.addChild(clone);
        });

        if (this.maskSprite != null) {
            let elementMask = new PIXI.Sprite((<PIXI.Sprite>this.maskSprite).texture);
            elementMask.position.x = this.maskSprite.position.x;
            elementMask.position.y = this.maskSprite.position.y;

            element.maskSprite = elementMask;
            element.addChild(elementMask);
            element.mask = elementMask;
        }

        element.markAsCloned(); // must be called last before return!
        return element;
    }

    private zerosPad(num: number, size: number): string {
        let s = num + "";
        while (s.length < size) s = "0" + s;
        return s;
    }

    public getBlendModeByString(mode: string): number {
        mode = mode.toString();
        switch (mode) {
            case "0":
            case "normal":
                return PIXI.BLEND_MODES.NORMAL;
            case "1":
            case "add":
                return PIXI.BLEND_MODES.ADD;
            case "2":
            case "multiply":
                return PIXI.BLEND_MODES.MULTIPLY;
            case "3":
            case "screen":
                return PIXI.BLEND_MODES.SCREEN;
            case "4":
            case "overlay":
                return PIXI.BLEND_MODES.OVERLAY;
            case "5":
            case "darken":
                return PIXI.BLEND_MODES.DARKEN;
            case "6":
            case "lighten":
                return PIXI.BLEND_MODES.LIGHTEN;
            case "7":
            case "color_dodge":
                return PIXI.BLEND_MODES.COLOR_DODGE;
            case "8":
            case "color_burn":
                return PIXI.BLEND_MODES.COLOR_BURN;
            case "9":
            case "hard_light":
                return PIXI.BLEND_MODES.HARD_LIGHT;
            case "10":
            case "soft_light":
                return PIXI.BLEND_MODES.SOFT_LIGHT;
            case "11":
            case "difference":
                return PIXI.BLEND_MODES.DIFFERENCE;
            case "12":
            case "exclusion":
                return PIXI.BLEND_MODES.EXCLUSION;
            case "13":
            case "hue":
                return PIXI.BLEND_MODES.HUE;
            case "14":
            case "saturation":
                return PIXI.BLEND_MODES.SATURATION;
            case "15":
            case "color":
                return PIXI.BLEND_MODES.COLOR;
            case "16":
            case "luminosity":
                return PIXI.BLEND_MODES.LUMINOSITY;
            default:
                if (DEBUG) {
                    this.log("ERROR Unknown blending mode " + mode + ". Used default mode NORMAL.");
                }
                return PIXI.BLEND_MODES.NORMAL;
        }
    }
}

export const AnimationAdditiveElementAttributes = {
    PLAY: "Play",
    START: "Start",
    STOP: "Stop",
    CONTINUE: "Continue",
    RESET: "Reset",
    LOOP: "Loop",
    SPEED: "Speed",

    HAS_MASK: "Masked",
    MASK_SUFFIX: "MaskSuffix",

    DELAY: "Delay",
    REVERSE: "Reverse",
    CLONE: "Clone",

    BLEND_MODE: "BlendMode",
    CLEAR_AFTER: "ClearAfter",
    STYLE: "Style",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/AnimationAdditiveElement.ts
 **/